import { useTranslation } from "@me8eon/translation";
import { useEffect, useMemo, useState } from "react";
import { useJwtToken } from "@me8eon/react_authentication";
import {
    EonUiDropdown,
    EonUiDropdownOption,
    EonUiInput,
    EonUiSwitchToggle,
    EonUiPagination,
    EonUiButton,
} from "@eon-ui/eon-ui-components-react";
import { OnboardingCompanyIds } from "../OnboardingPortal.constants";
import useOnboardingPortalStore from "../OnboardingPortal.Store";
import {
    decodeCompanyOptionValue,
    encodeCompanyOptionValue,
    getCompanyCanonicalKey,
    getCompanyNameFromId,
    isOtherSelected,
} from "../OnboardingPortal.utils";
import { useLanguage } from "@me8eon/language";
import { EnvironmentConfig } from "@me8eon/config";
import OnboardingPortalAppIcon from "../components/OnboardingPortalAppIcon";
import OnboardingPortalTableHeaders from "../components/OnboardingPortalTableHeaders";
import OnboardingPortalSearchFilter from "../components/OnboardingPortalSearchFilter";
import OnboardingPortalLoader from "../components/OnboardingPortalLoader";
import OnboardingPortalRefreshButton from "../components/OnboardingPortalRefreshButton";
import Modal from "me-at-eon/src/components/modal/Modal";

const normalizeCompanyName = (value: string | undefined | null): string =>
    getCompanyCanonicalKey(value);

const toNormalizedSet = (values: string[]): Set<string> =>
    new Set(
        values
            .map((value) => normalizeCompanyName(value))
            .filter((value) => value.length > 0)
    );

const DefaultAppsScreen = () => {
    const t = useTranslation();
    const jwt = useJwtToken();
    const { showModule, defaultApps, fetchDefaultApps, isLoadingDefaultApps } =
        useOnboardingPortalStore();
    const [lang] = useLanguage();
    const [search, setSearch] = useState("");
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const filteredApps = useMemo(
        () =>
            defaultApps.filter((app) => {
                const matchesSearch =
                    app.title.toLowerCase().includes(search.toLowerCase()) ||
                    app.subTitle.toLowerCase().includes(search.toLowerCase());
                return matchesSearch;
            }),
        [search, defaultApps]
    );
    const [companies, setCompanies] = useState<string>("");
    const [companiesOther, setCompaniesOther] = useState<string>("");
    const [appChanges, setAppChanges] = useState<Record<string, string>>({});
    const selectedCompany = decodeCompanyOptionValue(companies).trim();
    const isOtherCompanySelected = isOtherSelected(companies);
    const selectedOtherCompany = companiesOther.trim();
    const effectiveCompany = isOtherCompanySelected
        ? selectedOtherCompany
        : selectedCompany;
    const hasCompanySelection = effectiveCompany.length > 0;
    const [showModal, setShowModal] = useState<boolean>(false);
    const [modalText, setModalText] = useState<string>("");

    useEffect(() => {
        if (showModule === "default-apps") {
            fetchDefaultApps(jwt);
        }
    }, [showModule]);

    const isCompanyAllowed = (
        selectedCompanyName: string,
        companiesAllowed: string[],
        companiesRestricted: string[]
    ): boolean => {
        const normalizedSelectedCompany =
            normalizeCompanyName(selectedCompanyName);
        if (!normalizedSelectedCompany) {
            return false;
        }

        const allowedSet = toNormalizedSet(companiesAllowed);
        const restrictedSet = toNormalizedSet(companiesRestricted);

        if (allowedSet.size === 0 && restrictedSet.size === 0) {
            return true;
        }

        if (restrictedSet.has(normalizedSelectedCompany)) {
            return false;
        }

        if (allowedSet.size === 0) {
            return true;
        }

        return allowedSet.has(normalizedSelectedCompany);
    };

    const isToggleChecked = (
        id: string,
        companiesAllowed: string[],
        companiesRestricted: string[]
    ): boolean => {
        if (Object.prototype.hasOwnProperty.call(appChanges, id)) {
            return appChanges[id] === "allowed";
        }
        return isCompanyAllowed(
            effectiveCompany,
            companiesAllowed,
            companiesRestricted
        );
    };

    const handleSubmit = async (
        id: string,
        activity: "allowed" | "restricted",
        appTitle: string
    ) => {
        const API_BASE_URL = EnvironmentConfig.nodeService;
        const API_ENDPOINT = `${API_BASE_URL}/service/onboarding/apps`;
        try {
            const token = await jwt();
            const response = await fetch(API_ENDPOINT, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    updateType: "default-app-activity",
                    app: {
                        entityId: id,
                        company: effectiveCompany,
                        activity,
                    },
                }),
            });
            if (!response.ok) {
                const errorBody = await response.json();
                throw new Error(
                    `Failed to set app data: ${response.status} ${response.statusText}${errorBody?.error ? ` - ${errorBody.error}` : ""}`
                );
            }
            const data: { isSuccess: boolean } = await response.json();
            if (data.isSuccess !== true) {
                console.warn(
                    "API call succeeded, but response not marked as success."
                );
            }
            const companyDisplayName =
                getCompanyNameFromId(effectiveCompany) || effectiveCompany;
            setShowModal(true);
            setModalText(
                `Request created for ${activity === "allowed" ? "allowing" : "restricting"} app "${appTitle}" for ${companyDisplayName} successfully.`
            );
        } catch (error) {
            console.error("Error in handleSubmit:", error);
            throw error;
        }
    };

    return (
        <>
            <Modal
                toggleModal={!!showModal}
                closeModal={() => {
                    setShowModal(false);
                    setModalText("");
                }}
                isCloseIcon={true}
                closeOnBackdrop={true}
            >
                <div className="onboarding-portal--modal">
                    <p style={{ fontSize: "18px" }}>
                        {modalText}
                    </p>
                    <EonUiButton
                        rank="primary"
                        size="small"
                        onClick={() => {
                            setShowModal(false);
                            setModalText("");
                        }}
                        text={"Ok"}
                    />
                </div>
            </Modal>
            <div className="onboarding-portal--content--card">
                {isLoadingDefaultApps && <OnboardingPortalLoader />}
                <div className="onboarding-portal--content--card--header">
                    <div className="onboarding-portal--content--card--header--text">
                        {t("onboardingPortal.tableHeaders.apps")} (
                        {filteredApps.length})
                    </div>

                    <div className="onboarding-portal--content--card--header--actions onboarding-portal--content--card--header--actions--default-apps">
                        <OnboardingPortalRefreshButton
                            onClick={() => fetchDefaultApps(jwt)}
                        />
                        <OnboardingPortalSearchFilter
                            t={t}
                            search={search}
                            setSearch={setSearch}
                            placeholder={t(
                                "onboardingPortal.defaultApps.searchPlaceholder"
                            )}
                        />
                        <EonUiDropdown
                            value={companies}
                            multiselect={false}
                            showSelectedItems
                            onValueChanged={(e) => {
                                setCompanies(e.target.value);
                                setAppChanges({});
                            }}
                            height={"300px"}
                            key={companies}
                            label={t(
                                "onboardingPortal.tableHeaders.selectCompany"
                            )}
                            size="small"
                        >
                            {OnboardingCompanyIds.map((companyId) => (
                                <EonUiDropdownOption
                                    key={companyId}
                                    value={encodeCompanyOptionValue(companyId)}
                                    label={getCompanyNameFromId(companyId)}
                                />
                            ))}
                            <EonUiDropdownOption value="Other" label="Other" />
                        </EonUiDropdown>
                        {isOtherCompanySelected && (
                            <EonUiInput
                                type="text"
                                value={companiesOther}
                                placeholder={t(
                                    "onboardingPortal.tableHeaders.otherCompany"
                                )}
                                onValueChanged={(e) => {
                                    setCompaniesOther(e.target.value);
                                    setAppChanges({});
                                }}
                                label={t(
                                    "onboardingPortal.tableHeaders.otherCompany"
                                )}
                                size="small"
                            />
                        )}
                    </div>
                </div>
                <table className="onboarding-portal--table">
                    <OnboardingPortalTableHeaders
                        headers={[
                            "onboardingPortal.tableHeaders.icon",
                            "onboardingPortal.tableHeaders.title",
                            "onboardingPortal.tableHeaders.subTitle",
                            "onboardingPortal.tableHeaders.description",
                            "onboardingPortal.tableHeaders.action",
                        ]}
                        t={t}
                    />
                    <tbody>
                        {filteredApps
                            .slice(
                                (currentPage - 1) * pageSize,
                                currentPage * pageSize
                            )
                            .map((app) => (
                                <tr key={`${app.id}_${effectiveCompany}`}>
                                    <td>
                                        <OnboardingPortalAppIcon
                                            icon={app.icon}
                                            iconUrl={app.iconDataUrl}
                                        />
                                    </td>
                                    <td>
                                        <a
                                            href={app.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {app.title}
                                        </a>
                                    </td>
                                    <td>{app.subTitle}</td>
                                    <td>
                                        {
                                            (app?.description ||
                                                app?.tooltip) as string
                                        }
                                    </td>
                                    <td>
                                        <div className="actions-row">
                                            <div>
                                                {companies &&
                                                    hasCompanySelection && (
                                                        <EonUiSwitchToggle
                                                            checked={isToggleChecked(
                                                                app.id ||
                                                                    app.appId,
                                                                app.companiesAllowed,
                                                                app.companiesRestricted
                                                            )}
                                                            name={
                                                                app.id ||
                                                                app.appId
                                                            }
                                                            onValueChanged={async (
                                                                e
                                                            ) => {
                                                                const baseStatus =
                                                                    isCompanyAllowed(
                                                                        effectiveCompany,
                                                                        app.companiesAllowed,
                                                                        app.companiesRestricted
                                                                    )
                                                                        ? "allowed"
                                                                        : "restricted";
                                                                const selectedStatus =
                                                                    e.target
                                                                        .checked
                                                                        ? "allowed"
                                                                        : "restricted";

                                                                // capture previous appChanges to allow revert on error
                                                                const prevChanges =
                                                                    {
                                                                        ...appChanges,
                                                                    } as Record<
                                                                        string,
                                                                        string
                                                                    >;

                                                                // optimistic update of local state
                                                                if (
                                                                    selectedStatus ===
                                                                    baseStatus
                                                                ) {
                                                                    setAppChanges(
                                                                        (
                                                                            prev
                                                                        ) => {
                                                                            if (
                                                                                !Object.prototype.hasOwnProperty.call(
                                                                                    prev,
                                                                                    app.id ||
                                                                                        app.appId
                                                                                )
                                                                            ) {
                                                                                return prev;
                                                                            }
                                                                            const updated =
                                                                                {
                                                                                    ...prev,
                                                                                };
                                                                            delete updated[
                                                                                app.id ||
                                                                                    app.appId
                                                                            ];
                                                                            return updated;
                                                                        }
                                                                    );
                                                                } else {
                                                                    setAppChanges(
                                                                        (
                                                                            prev
                                                                        ) => ({
                                                                            ...prev,
                                                                            [app.id ||
                                                                            app.appId]:
                                                                                selectedStatus,
                                                                        })
                                                                    );
                                                                }

                                                                try {
                                                                    await handleSubmit(
                                                                        app.id ||
                                                                            app.appId,
                                                                        selectedStatus,
                                                                        app.title
                                                                    );
                                                                } catch (err) {
                                                                    // revert optimistic change on error
                                                                    console.error(
                                                                        "Failed to submit toggle change:",
                                                                        err
                                                                    );
                                                                    setAppChanges(
                                                                        prevChanges
                                                                    );
                                                                    alert(
                                                                        `Failed to update app: ${err}`
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    )}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
            <EonUiPagination
                lang={lang}
                begin={
                    filteredApps.length === 0 ? 0 : (currentPage - 1) * pageSize
                }
                currentPage={currentPage}
                end={Math.min(currentPage * pageSize, filteredApps.length)}
                initialPageSize={pageSize}
                pageSize={pageSize}
                pageSizes={"5,10,20,50,100"}
                totalItems={filteredApps.length}
                onPageChanged={(e) => {
                    setCurrentPage(e.detail as any);
                    console.log(e.detail);
                }}
                onPageSizeChanged={(e) => {
                    setPageSize(e.detail);
                    setCurrentPage(1);
                }}
                onPageDecreased={(e) => {
                    console.log(e);
                    setCurrentPage((prev) => Math.max(prev - 1, 1));
                }}
                onPageIncreased={(e) => {
                    console.log(e);
                    setCurrentPage((prev) =>
                        Math.min(
                            prev + 1,
                            Math.ceil(filteredApps.length / pageSize)
                        )
                    );
                }}
            />

            {/* <div style={{ display: "flex", flexDirection: "row", gap: 12 }}>
                <EonUiButton
                    rank="primary"
                    onClick={() =>
                        console.log(
                            "Save changes clicked with appChanges:",
                            appChanges
                        )
                    }
                    text="Save Changes"
                />
                <EonUiButton
                    rank="secondary"
                    onClick={() => {
                        setCompanies("");
                        setCompaniesOther("");
                        setAppChanges({});
                    }}
                    text="Reset"
                />
            </div> */}
        </>
    );
};

export default DefaultAppsScreen;
